import { useMemo } from "react";
import { MasterIndex } from "verdi-web-client";
import { useListDataStreams } from "@/api/orval/generated/data-stream";
import { type DashboardSpec, SPEC_VERSION, type TimeAxisPanelSpec } from "../ChartSpec";
import { DEFAULT_RELATIVE_DURATION } from "../time-window/time-range-slice";
import { buildCategoryPanel, buildTwoAxisPanel } from "./panel-builders";
import { type ResolvedStream, resolveStreams } from "./resolve-streams";

export function getZoneSpec(resolvedStreams: ResolvedStream[]): DashboardSpec {
    const defaultTimeRange = { mode: "relative" as const, duration: DEFAULT_RELATIVE_DURATION };

    if (resolvedStreams.length === 0) {
        return { version: SPEC_VERSION, defaultTimeRange, panels: [] };
    }

    // Roll up all streams by their unit categories, so we can display them on the same panel
    const streamsByUnitCategory = resolvedStreams.reduce<Map<string, ResolvedStream[]>>((acc, resolvedStream) => {
        const existing = acc.get(resolvedStream.unitCategory);
        if (existing) existing.push(resolvedStream);
        else acc.set(resolvedStream.unitCategory, [resolvedStream]);
        return acc;
    }, new Map());

    // If we only have two unit categories we can display them with a dual-axis panel
    if (streamsByUnitCategory.size === 2) {
        const [leftStreams, rightStreams] = streamsByUnitCategory.values();
        const twoAxisPanel = buildTwoAxisPanel(leftStreams, rightStreams);
        if (twoAxisPanel) {
            return {
                version: SPEC_VERSION,
                defaultTimeRange,
                panels: [twoAxisPanel],
            };
        }
    }

    // Otherwise just create a panel per-unit category
    const panels: TimeAxisPanelSpec[] = [];

    streamsByUnitCategory.forEach((categoryStreams, unitCategory) => {
        panels.push(buildCategoryPanel(unitCategory, categoryStreams));
    });

    return { version: SPEC_VERSION, defaultTimeRange, panels };
}

/**
 * Think of this as the configuration file for how a zone-based DataViewer should be rendered.
 *
 * @param zoneId - The ID of the zone to build a spec for.
 * @returns {
 *     spec: DashboardSpec;
 *     isLoading: boolean;
 *     isError: boolean;
 *     isEmpty: boolean;
 * }
 */
export function useZoneDataViewerSpec(zoneId: string): {
    spec: DashboardSpec;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
} {
    const { data: dataStreams, isLoading, isError } = useListDataStreams({ zoneId }, { query: { enabled: !!zoneId } });
    const resolvedStreams: ResolvedStream[] = useMemo(() => {
        const devices = MasterIndex.zone.byID[zoneId]?.devices;
        return dataStreams ? resolveStreams(dataStreams, devices) : [];
    }, [dataStreams, zoneId]);

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(() => getZoneSpec(resolvedStreams), [resolvedStreams]);

    return {
        spec,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
    };
}
